import { onMounted, onUnmounted } from 'vue'

// 图片懒加载
export const lazyLoad = (el: HTMLImageElement, src: string) => {
	const observer = new IntersectionObserver(entries => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				el.src = src
				observer.unobserve(el)
			}
		})
	})
	observer.observe(el)
}

// 防抖函数
export const debounce = (fn: Function, delay: number) => {
	let timer: NodeJS.Timeout | null = null
	return function (...args: any[]) {
		if (timer) {
			clearTimeout(timer)
		}
		timer = setTimeout(() => {
			fn.apply(this, args)
		}, delay)
	}
}

// 节流函数
export const throttle = (fn: Function, delay: number) => {
	let last = 0
	return function (...args: any[]) {
		const now = Date.now()
		if (now - last > delay) {
			fn.apply(this, args)
			last = now
		}
	}
}

// 组件性能监控
export const usePerformanceMonitor = (componentName: string) => {
	onMounted(() => {
		console.time(`${componentName}-mount`)
	})

	onUnmounted(() => {
		console.timeEnd(`${componentName}-mount`)
	})
}

// 资源预加载
export const preloadResource = (url: string) => {
	const link = document.createElement('link')
	link.rel = 'preload'
	link.as = 'fetch'
	link.href = url
	document.head.appendChild(link)
}

// 检查浏览器支持
export const checkBrowserSupport = () => {
	const features = {
		webp: false,
		webgl: false,
		webworkers: false,
		localStorage: false
	}

	// 检查 WebP 支持
	const webp = new Image()
	webp.onload = function () {
		features.webp = webp.width > 0 && webp.height > 0
	}
	webp.src = 'data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA'

	// 检查 WebGL 支持
	try {
		const canvas = document.createElement('canvas')
		features.webgl = !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')))
	} catch (e) {
		features.webgl = false
	}

	// 检查 Web Workers 支持
	features.webworkers = typeof Worker !== 'undefined'

	// 检查 localStorage 支持
	try {
		localStorage.setItem('test', 'test')
		localStorage.removeItem('test')
		features.localStorage = true
	} catch (e) {
		features.localStorage = false
	}

	return features
}

// 性能数据收集
export const collectPerformanceMetrics = () => {
	const metrics = {
		// 页面加载时间
		loadTime: performance.timing.loadEventEnd - performance.timing.navigationStart,
		// DOM 解析时间
		domReady: performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart,
		// 首次内容绘制
		fcp: performance.getEntriesByType('paint').find(entry => entry.name === 'first-contentful-paint')?.startTime,
		// 最大内容绘制
		lcp: performance.getEntriesByType('largest-contentful-paint').pop()?.startTime,
		// 首次输入延迟
		fid: performance.getEntriesByType('first-input').pop()?.duration,
		// 累积布局偏移
		cls: performance.getEntriesByType('layout-shift').reduce((sum, entry) => sum + (entry as any).value, 0)
	}

	return metrics
}
